/**
 * Module Configuration
 *
 * This file defines all available modules in the multi-module system.
 * Each module has its own metadata, navigation items, and visual styling.
 */

// =============================================================================
// Types
// =============================================================================

export type ModuleStatus = 'active' | 'coming_soon' | 'disabled';

export interface ModuleNavItem {
  path: string;
  label: string;
  adminOnly?: boolean;
  /**
   * When true, the nav item is also considered "active" while the user is
   * on any sub-path of `path`. Defaults to strict equality.
   */
  matchPrefix?: boolean;
  /**
   * Minimum module access level required to see this nav item.
   * When omitted, the item is visible to anyone with module access (view+).
   */
  minLevel?: 'view' | 'edit' | 'admin';
  /**
   * For tool-governed modules: the tool that governs this nav item.
   * The item is visible only with explicit tool access.
   */
  toolId?: string;
  /** When true, the tab is shown but not clickable (e.g. coming soon). */
  disabled?: boolean;
  /** When set, item is only visible if this feature flag is enabled (key name in FeatureFlags). */
  featureFlag?: string;
  /** When set, item is visible only to these emails (lowercase). */
  allowedEmails?: string[];
}

/**
 * Tool definition for modules that have granular permissions
 * (e.g., a module that exposes multiple sub-tools with their own access levels)
 */
export interface ModuleTool {
  id: string;
  name: string;
  description: string;
  path: string;
  icon?: string;
  status?: 'active' | 'coming_soon';
  /** When set, tool is only visible if this feature flag is enabled (key name in FeatureFlags) */
  featureFlag?: string;
}

export interface ModuleConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
  status: ModuleStatus;
  color: {
    primary: string;
    gradient: string;
  };
  navItems: ModuleNavItem[];
  /**
   * Optional list of tools within the module.
   * When defined, permissions can be set per-tool instead of per-module.
   */
  tools?: ModuleTool[];
}

// =============================================================================
// Icons (SVG path data)
// =============================================================================

const ICONS = {
  // Banknotes icon - for the finance module
  finance: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
  // Users icon - for user management (kept for shared usage)
  users: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
} as const;

// =============================================================================
// Module Definitions
// =============================================================================

export const modules: ModuleConfig[] = [
  {
    id: 'finance',
    name: 'כספים',
    description: 'ניהול המידע הכספי של הגמ"ח',
    icon: ICONS.finance,
    path: '/finance',
    status: 'active',
    color: {
      primary: 'blue',
      gradient: 'from-blue-500 to-blue-700',
    },
    navItems: [
      { path: '/finance', label: 'ראשי' },
    ],
  },
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get a module configuration by its ID
 */
export const getModuleById = (id: string): ModuleConfig | undefined => {
  return modules.find((module) => module.id === id);
};

/**
 * Get a module configuration by matching the start of a path
 */
export const getModuleByPath = (path: string): ModuleConfig | undefined => {
  return modules.find((module) => path.startsWith(module.path));
};

/**
 * Get only modules with 'active' status
 */
export const getActiveModules = (): ModuleConfig[] => {
  return modules.filter((module) => module.status === 'active');
};

/**
 * Get modules that have tools defined
 */
export const getModulesWithTools = (): ModuleConfig[] => {
  return modules.filter((module) => module.tools && module.tools.length > 0);
};

/**
 * Check if a module has granular tool permissions
 */
export const moduleHasTools = (moduleId: string): boolean => {
  const module = getModuleById(moduleId);
  return module?.tools !== undefined && module.tools.length > 0;
};

// =============================================================================
// Status Labels (Hebrew)
// =============================================================================

export const statusLabels: Record<ModuleStatus, string> = {
  active: 'פעיל',
  coming_soon: 'בקרוב',
  disabled: 'לא זמין',
};
